import type {
  Flow_AbstractComponent,
  Flow_Diff,
  Flow_ComponentProps,
  Flow_ObjMap,
} from 'src/types/flow-to-typescript-codemod';
import type {CustomFormField} from 'src/types/crm';
import type {FieldClassNames} from 'src/components/crm/jobs/crud/job-details/custom-form/custom-form';
import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {debounce} from 'lodash';
import {Field} from 'formik';
import {
  InputField,
  TextAreaField,
  BooleanRadioGroup,
  DateRangeComponent,
  SingleSelectDropDown,
  MultiSelectDropDown,
  NumberRangeComponent,
  FileUploadComponent,
} from 'src/components/crm/jobs/crud/job-details/custom-form/fields/index';
import {TypeAheadSelector} from 'src/components/crm/lib/typeAhead/typeahead';
import {ButtonTextSmall} from '@spaced-out/ui-design-system/lib/components/Text';

import css from './custom-form.css';


type FormikProps = {
  errors: {
    [key: string]: unknown;
  };
  touched: {
    [key: string]: unknown;
  };
  setFieldValue: (arg1: string, arg2: unknown) => void;
  initialValues: any;
  values: {
    [key: string]: unknown;
  };
};

type DynamicFieldProps = {
  field: CustomFormField;
  formikProps: FormikProps;
  hasInitialValues: boolean;
  classNames?: FieldClassNames;
  showFormTypes?: Array<string>;
  required?: boolean;
  dateRangePlacement?: string;
  isCustomFilter?: boolean;
};

const DynamicFieldBase = ({
  field,
  formikProps,
  hasInitialValues,
  classNames,
  showFormTypes = [],
  required = true,
  dateRangePlacement,
  isCustomFilter = false,
}: DynamicFieldProps) => {
  const {t} = useLabelTranslation();
  const {fieldType} = field.customField;
  const {errors, touched, setFieldValue, values} = formikProps;

  const fieldIdRef = React.useRef(field.customFieldId);
  const fieldName = `custom_${field.customFieldId}`;
  const isRequired = required && field.show === 'MANDATORY';

  // Get the current value from Formik's values (current form state)
  const prefilledValue = values?.[fieldName];

  // Sync localValue with Formik field value
  const [localValue, setLocalValue] = React.useState(prefilledValue);

  React.useEffect(() => {
    fieldIdRef.current = field.customFieldId;
  }, [field.customFieldId]);

  // Sync localValue when the Formik field value changes externally
  React.useEffect(() => {
    setLocalValue(prefilledValue);
  }, [prefilledValue]);

  const debouncedSetFieldValue = React.useCallback(
    debounce((name, value) => setFieldValue(name, value), 400),
    [setFieldValue],
  );

  const handleInputChange = (name: string, value: unknown) => {
    setLocalValue(value);
    debouncedSetFieldValue(name, value);
  };

  const handleSelectChange = React.useCallback(
    // @ts-expect-error - TS7006 - Parameter 'newValues' implicitly has an 'any' type.
    (newValues) => {
      const name = `custom_${fieldIdRef.current}`;
      setFieldValue(name, newValues);
    },
    [setFieldValue],
  );

  const handleDateRangeChange = React.useCallback(
    // @ts-expect-error - TS7031 - Binding element 'startDate' implicitly has an 'any' type. | TS7031 - Binding element 'endDate' implicitly has an 'any' type.
    ({startDate, endDate}) => {
      const name = `custom_${fieldIdRef.current}`;
      const rangeValue = [startDate || null, endDate || null];
      setFieldValue(name, rangeValue);
    },
    [setFieldValue],
  );

  const handleNumberRangeChange = React.useCallback(
    // @ts-expect-error - TS7031 - Binding element 'minValue' implicitly has an 'any' type. | TS7031 - Binding element 'maxValue' implicitly has an 'any' type.
    ({minValue, maxValue}) => {
      const name = `custom_${fieldIdRef.current}`;
      const rangeValue = [
        minValue === '' ? null : Number(minValue),
        maxValue === '' ? null : Number(maxValue),
      ];
      setFieldValue(name, rangeValue);
    },
    [setFieldValue],
  );

  const handleUserSelectChange = React.useCallback(
    // @ts-expect-error - TS7006 - Parameter 'selectedUsers' implicitly has an 'any' type.
    (selectedUsers) => {
      const name = `custom_${fieldIdRef.current}`;
      if (
        selectedUsers &&
        (Array.isArray(selectedUsers)
          ? selectedUsers.length > 0
          : selectedUsers)
      ) {
        setFieldValue(name, selectedUsers);
      } else {
        setFieldValue(name, null);
      }
    },
    [setFieldValue],
  );

  const handleClear = React.useCallback(() => {
    const name = `custom_${fieldIdRef.current}`;
    setFieldValue(name, null);
    setLocalValue('');
  }, [setFieldValue]);

  // Validate field first
  if (!field.customField) {
    return null;
  }

  if (
    showFormTypes &&
    showFormTypes.length > 0 &&
    !showFormTypes.includes(fieldType)
  ) {
    return null;
  }

  const renderClearButton = (value: any, fieldType: string) => {
    if (field.show === 'DISABLED') {
      return null;
    }

    // For range fields, check if the array contains meaningful values
    if (fieldType === 'DATE_RANGE' && Array.isArray(value)) {
      const hasValidDates = value.some(
        (date) => date && !isNaN(new Date(date).getTime()),
      );
      if (!hasValidDates) {
        return null;
      }
    } else if (
      (fieldType === 'NUMBER_RANGE' || fieldType === 'CURRENCY_RANGE') &&
      Array.isArray(value)
    ) {
      const hasValidNumbers = value.some(
        (num) => num !== null && num !== undefined && num !== '',
      );
      if (!hasValidNumbers) {
        return null;
      }
    } else if (!value) {
      return null;
    }

    return (
      <ButtonTextSmall
        color="danger"
        // @ts-expect-error - TS2769 - No overload matches this call.
        onClick={handleClear}
        className={css.clearButton}
      >
        {t('Clear', 'Clear')}
      </ButtonTextSmall>
    );
  };

  const possibleValueToIdMap = new Map(
    (field.customField.possibleValues || []).map((opt) => [
      opt.id.toString(),
      opt.possibleValue,
    ]),
  );

  switch (fieldType) {
    case 'TEXT':
      if (isCustomFilter) {
        return (
          <Field name={fieldName}>
            {/* @ts-expect-error - TS7031 - Binding element 'formikField' implicitly has an 'any' type. */}
            {({field: formikField}) => {
              const value = Array.isArray(formikField.value)
                ? formikField.value
                : typeof formikField.value === 'string' &&
                  formikField.value.includes(',')
                ? formikField.value
                    .split(',')
                    // @ts-expect-error - TS7006 - Parameter 'v' implicitly has an 'any' type.
                    .map((v) => v.trim())
                    .filter(Boolean)
                : formikField.value
                ? [formikField.value]
                : [];
              // @ts-expect-error - TS7006 - Parameter 'key' implicitly has an 'any' type.
              const selectedOption = value.map((key) => {
                return {key, label: key};
              });
              return (
                <div className={css.flexColumn}>
                  <TypeAheadSelector
                    label={field.questionTag || field.customField.name}
                    placeholder={t(
                      'Type_to_select_value',
                      'Type to select value',
                    )}
                    typeAheadOptions={[]}
                    selectedOption={selectedOption}
                    onSelectOption={(options) => {
                      setFieldValue(
                        fieldName,
                        options.map((opt) => opt.key),
                      );
                    }}
                    required={isRequired}
                    error={Boolean(required && errors[fieldName])}
                    errorText={
                      required && errors[fieldName]
                        ? String(errors[fieldName])
                        : ''
                    }
                    isSingleSelect={false}
                  />
                  {renderClearButton(formikField.value, fieldType)}
                </div>
              );
            }}
          </Field>
        );
      } else {
        return (
          <Field name={fieldName}>
            {/* @ts-expect-error - TS7031 - Binding element 'formikField' implicitly has an 'any' type. */}
            {({field: formikField}) => (
              <div className={css.flexColumn}>
                <InputField
                  field={field}
                  label={field.questionTag || field.customField.name}
                  formikField={formikField}
                  onChange={handleInputChange}
                  value={localValue as string}
                  isRequired={isRequired}
                  error={
                    required && errors[fieldName]
                      ? String(errors[fieldName])
                      : null
                  }
                  touched={Boolean(touched[fieldName] || hasInitialValues)}
                  classNames={classNames?.textInput}
                  disabled={field.show === 'DISABLED'}
                />
                {renderClearButton(formikField.value, fieldType)}
              </div>
            )}
          </Field>
        );
      }

    case 'TEXT_AREA':
      return (
        <Field name={fieldName}>
          {/* @ts-expect-error - TS7031 - Binding element 'formikField' implicitly has an 'any' type. */}
          {({field: formikField}) => (
            <div className={css.flexColumn}>
              <TextAreaField
                field={field}
                label={field.questionTag || field.customField.name}
                formikField={formikField}
                onChange={handleInputChange}
                value={localValue as string}
                isRequired={isRequired}
                error={
                  required && errors[fieldName]
                    ? String(errors[fieldName])
                    : null
                }
                touched={Boolean(touched[fieldName] || hasInitialValues)}
                classNames={classNames?.textAreaInput}
                disabled={field.show === 'DISABLED'}
              />
              {renderClearButton(formikField.value, fieldType)}
            </div>
          )}
        </Field>
      );

    case 'MULTI_SELECT': {
      const typeAheadOptions = (field.customField.possibleValues || []).map(
        (opt) => ({
          key: opt.id.toString(),
          label: opt.possibleValue,
        }),
      );
      return (
        <Field name={fieldName}>
          {/* @ts-expect-error - TS7031 - Binding element 'formikField' implicitly has an 'any' type. */}
          {({field: formikField}) => {
            const value = Array.isArray(formikField.value)
              ? formikField.value.filter(Boolean)
              : formikField.value
              ? [formikField.value].filter(Boolean)
              : [];
            const selectedOption = value.map((possibleValue: string) => {
              const found = typeAheadOptions.find(
                (opt) => opt.label === possibleValue,
              );
              return found || {key: possibleValue, label: possibleValue};
            });

            return (
              <div className={css.flexColumn}>
                <TypeAheadSelector
                  label={field.questionTag || field.customField.name}
                  placeholder={t(
                    'Type_to_select_value',
                    'Type to select value',
                  )}
                  typeAheadOptions={typeAheadOptions}
                  selectedOption={selectedOption}
                  doNotShowOptionsIfNoResults={true}
                  onSelectOption={(options) => {
                    setFieldValue(
                      fieldName,
                      options
                        .map(
                          (opt) => possibleValueToIdMap.get(opt.key) ?? opt.key,
                        )
                        .filter(Boolean),
                    );
                  }}
                  required={isRequired}
                  error={Boolean(required && errors[fieldName])}
                  errorText={
                    required && errors[fieldName]
                      ? String(errors[fieldName])
                      : ''
                  }
                  isSingleSelect={false}
                  disabled={field.show === 'DISABLED'}
                />
                {renderClearButton(formikField.value, fieldType)}
              </div>
            );
          }}
        </Field>
      );
    }
    case 'SINGLE_SELECT': {
      const typeAheadOptions = (field.customField.possibleValues || []).map(
        (opt) => ({
          key: opt.id.toString(),
          label: opt.possibleValue,
        }),
      );
      return (
        <Field name={fieldName}>
          {/* @ts-expect-error - TS7031 - Binding element 'formikField' implicitly has an 'any' type. */}
          {({field: formikField}) => {
            const value = Array.isArray(formikField.value)
              ? formikField.value
              : typeof formikField.value === 'string' &&
                formikField.value.includes(',')
              ? formikField.value
                  .split(',')
                  // @ts-expect-error - TS7006 - Parameter 'v' implicitly has an 'any' type.
                  .map((v) => v.trim())
                  .filter(Boolean)
              : formikField.value
              ? [formikField.value]
              : [];
            const selectedOption = value.map((possibleValue: string) => {
              const found = typeAheadOptions.find(
                (opt) => opt.label === possibleValue,
              );
              return found || {key: possibleValue, label: possibleValue};
            });

            return (
              <div className={css.flexColumn}>
                <TypeAheadSelector
                  label={field.questionTag || field.customField.name}
                  placeholder={t(
                    'Type_to_select_value',
                    'Type to select value',
                  )}
                  doNotShowOptionsIfNoResults={true}
                  typeAheadOptions={typeAheadOptions}
                  selectedOption={selectedOption}
                  disabled={field.show === 'DISABLED'}
                  onSelectOption={(options) => {
                    const values = options.map(
                      (opt) => possibleValueToIdMap.get(opt.key) ?? opt.key,
                    );
                    setFieldValue(
                      fieldName,
                      isCustomFilter ? values : values[0] || null,
                    );
                  }}
                  required={isRequired}
                  error={Boolean(required && errors[fieldName])}
                  errorText={
                    required && errors[fieldName]
                      ? String(errors[fieldName])
                      : ''
                  }
                  isSingleSelect={isCustomFilter ? false : true}
                />
                {renderClearButton(formikField.value, fieldType)}
              </div>
            );
          }}
        </Field>
      );
    }

    case 'SINGLE_SELECT_OLD': {
      const typeAheadOptions = (field.customField.possibleValues || []).map(
        (opt) => ({
          key: opt.id.toString(),
          label: opt.possibleValue,
        }),
      );
      return (
        <Field name={fieldName}>
          {/* @ts-expect-error - TS7031 - Binding element 'formikField' implicitly has an 'any' type. */}
          {({field: formikField}) => {
            const value = Array.isArray(formikField.value)
              ? formikField.value
              : typeof formikField.value === 'string' &&
                formikField.value.includes(',')
              ? formikField.value
                  .split(',')
                  // @ts-expect-error - TS7006 - Parameter 'v' implicitly has an 'any' type.
                  .map((v) => v.trim())
                  .filter(Boolean)
              : formikField.value
              ? [formikField.value]
              : [];
            const selectedOption = value.map((possibleValue: string) => {
              const found = typeAheadOptions.find(
                (opt) => opt.label === possibleValue,
              );
              return found || {key: possibleValue, label: possibleValue};
            });

            return (
              <div className={css.flexColumn}>
                <TypeAheadSelector
                  label={field.questionTag || field.customField.name}
                  placeholder={t(
                    'Type_to_select_value',
                    'Type to select value',
                  )}
                  doNotShowOptionsIfNoResults={true}
                  typeAheadOptions={typeAheadOptions}
                  selectedOption={selectedOption}
                  disabled={field.show === 'DISABLED'}
                  onSelectOption={(options) => {
                    if (!options || options.length === 0) {
                      setFieldValue(fieldName, null);
                      setLocalValue(null);
                    } else {
                      const values =
                        options.map(
                          (opt) => possibleValueToIdMap.get(opt.key) ?? opt.key,
                        ) || [];
                      setFieldValue(
                        fieldName,
                        isCustomFilter
                          ? values
                          : values.length > 0
                          ? values[0]
                          : null,
                      );
                    }
                  }}
                  required={isRequired}
                  error={Boolean(required && errors[fieldName])}
                  errorText={
                    required && errors[fieldName]
                      ? String(errors[fieldName])
                      : ''
                  }
                  isSingleSelect={isCustomFilter ? false : true}
                />
                {renderClearButton(formikField.value, fieldType)}
              </div>
            );
          }}
        </Field>
      );
    }

    case 'BOOLEAN':
      return (
        <Field name={fieldName}>
          {/* @ts-expect-error - TS7031 - Binding element 'formikField' implicitly has an 'any' type. */}
          {({field: formikField}) => (
            <div className={css.flexColumn}>
              <BooleanRadioGroup
                field={field}
                label={field.questionTag || field.customField.name}
                formikField={formikField}
                onChange={setFieldValue}
                isRequired={isRequired}
                error={
                  required && errors[fieldName]
                    ? String(errors[fieldName])
                    : null
                }
                touched={Boolean(touched[fieldName] || hasInitialValues)}
                classNames={classNames?.booleanInput}
                disabled={field.show === 'DISABLED'}
              />
              {renderClearButton(formikField.value, fieldType)}
            </div>
          )}
        </Field>
      );

    case 'DATE':
      // Convert date value safely - handle both Unix timestamp and date string formats
      let dateValue = '';
      if (localValue) {
        // Check if it's already a date string (YYYY-MM-DD format)
        if (
          typeof localValue === 'string' &&
          /^\d{4}-\d{2}-\d{2}$/.test(localValue)
        ) {
          // Validate the date string
          const date = new Date(localValue);
          if (!isNaN(date.getTime())) {
            dateValue = localValue;
          }
        } else {
          // Try to convert from Unix timestamp (seconds)
          const timestamp = Number(localValue);
          if (!isNaN(timestamp) && timestamp > 0) {
            const date = new Date(timestamp * 1000);
            if (!isNaN(date.getTime())) {
              dateValue = date.toISOString().split('T')[0];
            }
          }
        }
      }

      // Custom handler for DATE fields to convert date string back to epoch
      const handleDateChange = (name: string, value: unknown) => {
        if (value && typeof value === 'string') {
          // Convert date string to epoch timestamp (seconds)
          const dateObj = new Date(value);
          if (!isNaN(dateObj.getTime())) {
            const epochValue = Math.floor(dateObj.getTime() / 1000).toString();
            setLocalValue(value); // Keep local display as date string
            setFieldValue(name, epochValue); // Store as epoch in form
          } else {
            setLocalValue(value);
            setFieldValue(name, value);
          }
        } else {
          setLocalValue(value);
          setFieldValue(name, value);
        }
      };

      return (
        <Field name={fieldName}>
          {/* @ts-expect-error - TS7031 - Binding element 'formikField' implicitly has an 'any' type. */}
          {({field: formikField}) => (
            <div className={css.flexColumn}>
              <InputField
                field={field}
                label={field.questionTag || field.customField.name}
                formikField={formikField}
                onChange={handleDateChange}
                value={dateValue}
                isRequired={isRequired}
                error={
                  required && errors[fieldName]
                    ? String(errors[fieldName])
                    : null
                }
                touched={Boolean(touched[fieldName] || hasInitialValues)}
                type="date"
                classNames={classNames?.textInput}
                disabled={field.show === 'DISABLED'}
              />
              {renderClearButton(formikField.value, fieldType)}
            </div>
          )}
        </Field>
      );

    case 'NUMBER':
    case 'CURRENCY':
      return (
        <Field name={fieldName}>
          {/* @ts-expect-error - TS7031 - Binding element 'formikField' implicitly has an 'any' type. */}
          {({field: formikField}) => (
            <div className={css.flexColumn}>
              <InputField
                field={field}
                label={field.questionTag || field.customField.name}
                formikField={formikField}
                onChange={handleInputChange}
                value={localValue as string}
                isRequired={isRequired}
                error={
                  required && errors[fieldName]
                    ? String(errors[fieldName])
                    : null
                }
                touched={Boolean(touched[fieldName] || hasInitialValues)}
                type="number"
                classNames={classNames?.numberRangeContainer}
                disabled={field.show === 'DISABLED'}
              />
              {renderClearButton(formikField.value, fieldType)}
            </div>
          )}
        </Field>
      );

    case 'DATE_RANGE':
      return (
        <Field name={fieldName}>
          {/* @ts-expect-error - TS7031 - Binding element 'formikField' implicitly has an 'any' type. */}
          {({field: formikField}) => {
            const startDate =
              formikField.value?.[0] &&
              !isNaN(new Date(formikField.value[0]).getTime())
                ? new Date(formikField.value[0]).toISOString().split('T')[0]
                : '';
            const endDate =
              formikField.value?.[1] &&
              !isNaN(new Date(formikField.value[1]).getTime())
                ? new Date(formikField.value[1]).toISOString().split('T')[0]
                : '';

            return (
              <div className={css.flexColumn}>
                <DateRangeComponent
                  formikField={formikField}
                  selectedDateRange={{startDate, endDate}}
                  setSelectedDateRange={handleDateRangeChange}
                  label={field.questionTag || field.customField.name}
                  isRequired={isRequired}
                  error={
                    required && errors[fieldName]
                      ? String(errors[fieldName])
                      : null
                  }
                  touched={Boolean(touched[fieldName] || hasInitialValues)}
                  classNames={classNames?.textInput}
                  placement={dateRangePlacement}
                  disabled={field.show === 'DISABLED'}
                />
                {renderClearButton(formikField.value, fieldType)}
              </div>
            );
          }}
        </Field>
      );

    case 'NUMBER_RANGE':
    case 'CURRENCY_RANGE':
      return (
        <Field name={fieldName}>
          {/* @ts-expect-error - TS7031 - Binding element 'formikField' implicitly has an 'any' type. */}
          {({field: formikField}) => {
            const minValue = formikField.value?.[0] ?? '';
            const maxValue = formikField.value?.[1] ?? '';

            return (
              <div className={`${css.numberRangeField} ${css.flexColumn}`}>
                <NumberRangeComponent
                  selectedRange={{minValue, maxValue}}
                  setSelectedRange={handleNumberRangeChange}
                  label={field.questionTag || field.customField.name}
                  isRequired={isRequired}
                  error={
                    required && errors[fieldName]
                      ? String(errors[fieldName])
                      : null
                  }
                  touched={Boolean(touched[fieldName] || hasInitialValues)}
                  formikField={formikField}
                  classNames={classNames?.numberRangeContainer}
                  disabled={field.show === 'DISABLED'}
                />
                {renderClearButton(formikField.value, fieldType)}
              </div>
            );
          }}
        </Field>
      );

    case 'SINGLE_USER_SELECT':
      return (
        <Field name={fieldName}>
          {/* @ts-expect-error - TS7031 - Binding element 'formikField' implicitly has an 'any' type. */}
          {({field: formikField}) => (
            <div className={css.flexColumn}>
              <TypeAheadSelector
                {...formikField}
                label={field.questionTag || field.customField.name}
                isApiCall
                isUserSearch
                typeAheadOptions={[]}
                placeholder={t('Type_to_select_value', 'Type to select value')}
                onSelectOption={handleUserSelectChange}
                selectedOption={formikField.value ?? []}
                isSingleSelect={true}
                required={isRequired}
                doNotShowOptionsIfNoResults={true}
                error={
                  required && errors[fieldName]
                    ? String(errors[fieldName])
                    : false
                }
                disabled={field.show === 'DISABLED'}
              />
              {renderClearButton(formikField.value, fieldType)}
            </div>
          )}
        </Field>
      );

    case 'MULTI_USER_SELECT':
      return (
        <Field name={fieldName}>
          {/* @ts-expect-error - TS7031 - Binding element 'formikField' implicitly has an 'any' type. */}
          {({field: formikField}) => (
            <div className={css.flexColumn}>
              <TypeAheadSelector
                isApiCall
                isUserSearch
                typeAheadOptions={[]}
                placeholder={t('Type_to_select_value', 'Type to select value')}
                onSelectOption={handleUserSelectChange}
                selectedOption={formikField.value ?? []}
                isSingleSelect={false}
                required={isRequired}
                label={field.questionTag || field.customField.name}
                doNotShowOptionsIfNoResults={true}
                // @ts-expect-error - TS2322 - Type 'string | false' is not assignable to type 'boolean | undefined'.
                error={
                  required && errors[fieldName]
                    ? String(errors[fieldName])
                    : false
                }
                disabled={field.show === 'DISABLED'}
              />
              {renderClearButton(formikField.value, fieldType)}
            </div>
          )}
        </Field>
      );

    case 'SINGLE_ATTACHMENT':
      return (
        <Field name={fieldName}>
          {/* @ts-expect-error - TS7031 - Binding element 'formikField' implicitly has an 'any' type. */}
          {({field: formikField}) => (
            <div className={css.flexColumn}>
              <FileUploadComponent
                formikField={formikField}
                label={field.questionTag || field.customField.name}
                field={field}
                isMultiFile={false}
                isRequired={isRequired}
                onChange={setFieldValue}
                error={
                  required && errors[fieldName]
                    ? String(errors[fieldName])
                    : null
                }
                touched={Boolean(touched[fieldName] || hasInitialValues)}
                classNames={classNames?.fileUpload}
                value={localValue as string}
                disabled={field.show === 'DISABLED'}
              />
              {renderClearButton(formikField.value, fieldType)}
            </div>
          )}
        </Field>
      );

    case 'MULTIPLE_ATTACHMENT':
      return (
        <Field name={fieldName}>
          {/* @ts-expect-error - TS7031 - Binding element 'formikField' implicitly has an 'any' type. */}
          {({field: formikField}) => (
            <div className={css.flexColumn}>
              <FileUploadComponent
                formikField={formikField}
                field={field}
                isMultiFile={true}
                isRequired={isRequired}
                onChange={setFieldValue}
                error={
                  required && errors[fieldName]
                    ? String(errors[fieldName])
                    : null
                }
                touched={Boolean(touched[fieldName] || hasInitialValues)}
                label={field.questionTag || field.customField.name}
                classNames={classNames?.fileUpload}
                value={localValue as string}
                disabled={field.show === 'DISABLED'}
              />
              {renderClearButton(formikField.value, fieldType)}
            </div>
          )}
        </Field>
      );

    default:
      return (
        <Field name={fieldName}>
          {/* @ts-expect-error - TS7031 - Binding element 'formikField' implicitly has an 'any' type. */}
          {({field: formikField}) => (
            <div className={css.flexColumn}>
              <InputField
                field={field}
                label={field.questionTag || field.customField.name}
                formikField={formikField}
                onChange={setFieldValue}
                isRequired={isRequired}
                error={
                  required && errors[fieldName]
                    ? String(errors[fieldName])
                    : null
                }
                classNames={classNames?.textInput}
                touched={Boolean(touched[fieldName] || hasInitialValues)}
                value={localValue as string}
                disabled={field.show === 'DISABLED'}
              />
              {renderClearButton(formikField.value, fieldType)}
            </div>
          )}
        </Field>
      );
  }
};

// Use basic React.memo to prevent unnecessary renders
export const DynamicField: Flow_AbstractComponent<DynamicFieldProps, unknown> =
  React.memo(DynamicFieldBase);
